import {DateTime} from 'luxon';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {StartEndData} from '../../../helpers/calendarHelpers';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {RecurrenceModel} from '../../../models/api/recurrenceModel';
import {DateComboBox} from '../../common/dateTime/dateComboBox';
import {TimeComboBox} from '../../common/dateTime/timeComboBox';
import {RecurrenceDropdownContainer} from './recurrenceEditor/recurrenceDropdownContainer';

/*
 * Props.
 */

interface CalendarEventStartEndEditorProps {
  startDateTime: DateTime;
  endDateTime: DateTime;
  isAllDay: boolean;
  timeFormat: TimeFormatsEnum;
  masterRecurrence?: Readonly<RecurrenceModel>;
  recurrence?: Readonly<RecurrenceModel>;
  onRecurrenceChange?: HandlerOf<RecurrenceModel | undefined>;
  onChange: HandlerOf<StartEndData>;
}

/*
 * Styles.
 */

const StyledContentWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 10px;
`;

const StyledInputRowWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-column-gap: 8px;
  align-items: center;
`;

const StyledDateTimeColumnDiv = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  grid-row-gap: 10px;
`;

const StyledTimeComboBox = styled(TimeComboBox)`
  margin: 0px;
`;

const StyledDateComboBox = styled(DateComboBox)`
  margin: 0px;
`;

const StyledMultiDayWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-column-gap: 6px;
  align-items: center;
`;

const StyledToDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
`;

const StyledArrowIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade60};
  transform: rotate(-90deg);
`;

const StyledOtherSettingsDiv = styled.div`
  height: 30px;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 15px;
  align-items: center;
`;

/*
 * Component.
 */

export const CalendarEventStartEndEditor: FC<CalendarEventStartEndEditorProps> = (props) => {
  const [isMultiDayChecked, setIsMultiDayChecked] = useState(false);
  const [shouldFocusEndDate, setShouldFocusEndDate] = useState(false);

  return (
    <StyledContentWrapperDiv>
      {renderInputs(
        props,
        isMultiDayChecked,
        setIsMultiDayChecked,
        shouldFocusEndDate,
        setShouldFocusEndDate,
      )}
      {renderOtherSettings(props, setShouldFocusEndDate)}
    </StyledContentWrapperDiv>
  );
};

function renderInputs(
  props: CalendarEventStartEndEditorProps,
  isMultiDayChecked: boolean,
  setIsMultiDayChecked: HandlerOf<boolean>,
  shouldFocusEndDate: boolean,
  setShouldFocusEndDate: HandlerOf<boolean>,
) {
  const {startDateTime, endDateTime, isAllDay, timeFormat} = props;

  const isSameDayStartEnd = startDateTime.toISODate() === endDateTime.toISODate();

  // According to the FullCalendar API, multiday events have the end date more than 1 day greater than the start date.
  const isDurationMultiDay = endDateTime.diff(startDateTime, 'days').days > 1;
  const isMultiDay = isMultiDayChecked || isDurationMultiDay;

  const onStartDateTimeSelect = (dateTime: DateTime | undefined, isDatePickerSelection?: boolean) => {
    if (!dateTime) {
      return;
    }

    const newStartDateTime = isDatePickerSelection
      ? dateTime.plus({hour: startDateTime.hour, minute: startDateTime.minute})
      : dateTime;

    // Update the end time to preserve the current duration.
    const duration = endDateTime.diff(startDateTime);
    props.onChange({
      startDateTime: newStartDateTime,
      endDateTime: newStartDateTime.plus(duration),
      isAllDay: props.isAllDay,
    });
  };

  const onEndDateTimeSelect = (dateTime: DateTime | undefined, isDatePickerSelection?: boolean) => {
    if (!dateTime) {
      return undefined;
    }

    const newEndDateTime = isDatePickerSelection
      ? // The datePicker returns time selections at midnight, so restore hour and minute values.
        dateTime.plus({
          day: isAllDay ? 1 : 0, // Adjust endDateTime for the all-day case.
          hour: endDateTime.hour,
          minute: endDateTime.minute,
        })
      : dateTime;

    // If the new end time is not before start, just change the end time.
    // For all day events, the endDateTime must be at least 1 day after the start.
    if (newEndDateTime >= (isAllDay ? startDateTime.plus({day: 1}) : startDateTime)) {
      return props.onChange({
        startDateTime,
        endDateTime: newEndDateTime,
        isAllDay: props.isAllDay,
      });
    }

    // If end is before start, adjust start to preserve the current duration.
    const duration = endDateTime.diff(startDateTime);
    return props.onChange({
      startDateTime: newEndDateTime.minus(duration),
      endDateTime: newEndDateTime,
      isAllDay,
    });
  };

  const dateFormat: Intl.DateTimeFormatOptions = isMultiDay
    ? {
        month: 'short',
        day: 'numeric',
      }
    : DateTime.DATE_FULL;

  const onMultiDayToggle = (newIsMultiDay: boolean) => {
    // If we're turning multi-day on, add a single day to endDateTime.
    // If we're turning multi-day off to an all day event, add a single day to startDateTime.
    // If we're turning multi-day off to a NOT all day event, reset endDateTime to 30 minutes after startDateTime.
    const endDate = () => {
      if (newIsMultiDay) {
        return endDateTime.plus({days: 1});
      }
      if (isAllDay) {
        return startDateTime.plus({days: 1});
      }
      return startDateTime.plus({minutes: 30});
    };

    props.onChange({
      startDateTime,
      endDateTime: endDate(),
      isAllDay,
    });

    // Focus on end date if we're turning multi-day on.
    setShouldFocusEndDate(newIsMultiDay);

    setIsMultiDayChecked(newIsMultiDay);
  };

  const startDateComboBox = (
    <StyledDateComboBox
      selectedDateTime={startDateTime}
      onDateSelect={(dateTime) => onStartDateTimeSelect(dateTime, true)}
      onMultiDayToggle={onMultiDayToggle}
      isMultiDay={isMultiDay}
      dateFormat={dateFormat}
    />
  );

  const endDateComboBox = (shouldFocusInput?: boolean) => (
    <StyledDateComboBox
      // Decrease endDateTime by one day for all day events b/c a single-day event should be
      // displayed as "Jan 1 - Jan 1" but represented as Jan 1 - Jan 2 on the data layer.
      selectedDateTime={isAllDay ? endDateTime.minus({day: 1}) : endDateTime}
      onDateSelect={(dateTime) => onEndDateTimeSelect(dateTime, true)}
      onMultiDayToggle={onMultiDayToggle}
      isMultiDay={isMultiDay}
      dateFormat={dateFormat}
      shouldFocusInput={shouldFocusInput}
    />
  );

  const startTimeComboBox = (
    <StyledTimeComboBox
      timeFormat={timeFormat}
      incrementMinutes={15}
      selectedTime={startDateTime}
      onTimeSelect={onStartDateTimeSelect}
      key={`start-time-combo-${startDateTime.valueOf()}`}
    />
  );

  // In the end time picker, hide time slots that are before the start time.
  const minTime = isSameDayStartEnd
    ? DateTime.local().startOf('day').set({hour: startDateTime.hour, minute: startDateTime.minute})
    : undefined;
  const endTimeComboBox = (
    <StyledTimeComboBox
      timeFormat={timeFormat}
      incrementMinutes={15}
      selectedTime={endDateTime}
      onTimeSelect={onEndDateTimeSelect}
      minTime={minTime}
      key={`end-time-combo-${endDateTime.valueOf()}`}
    />
  );

  // Displays date inputs in 4 possible configurations:
  // 1. All-day event (non multi-day): display a single DateComboBox
  // 2. All-day event (multi-day): display a DateComboBox for start and end
  // 3. Standard event (non multi-day): display a DateComboBox and two TimeComboBox
  // 4. Standard event (multi-day): display a two sets of DateComboBox + TimeComboBox
  if (isAllDay) {
    if (isMultiDay) {
      return (
        <StyledInputRowWrapperDiv>
          {startDateComboBox}
          <StyledArrowIcon name="arrowSmall" />
          {endDateComboBox(shouldFocusEndDate)}
        </StyledInputRowWrapperDiv>
      );
    }

    return startDateComboBox;
  }

  if (isMultiDay) {
    return (
      <StyledMultiDayWrapperDiv>
        <StyledDateTimeColumnDiv>
          {startDateComboBox}
          {startTimeComboBox}
        </StyledDateTimeColumnDiv>
        <StyledArrowIcon name="arrowSmall" />
        <StyledDateTimeColumnDiv>
          {endDateComboBox(shouldFocusEndDate)}
          {endTimeComboBox}
        </StyledDateTimeColumnDiv>
      </StyledMultiDayWrapperDiv>
    );
  }

  return (
    <>
      {startDateComboBox}
      <StyledInputRowWrapperDiv>
        {startTimeComboBox}
        <StyledToDiv>
          <Trans id="calendar-date-editor-to">to</Trans>
        </StyledToDiv>
        {endTimeComboBox}
      </StyledInputRowWrapperDiv>
    </>
  );
}

function renderOtherSettings(
  props: CalendarEventStartEndEditorProps,
  setShouldFocusEndDate: HandlerOf<boolean>,
) {
  const {startDateTime, endDateTime, isAllDay} = props;

  return (
    <StyledOtherSettingsDiv>
      <CheckBox
        isChecked={isAllDay}
        onChange={(newIsAllDay) => {
          // Prevent focusing on end date.
          setShouldFocusEndDate(false);

          // If we're switching to an all-day event, set the end to one day after start.
          // If we're switching to a non-all-day event, reset the end date to 30 minutes after start.
          props.onChange({
            startDateTime,
            endDateTime: newIsAllDay ? endDateTime.plus({days: 1}) : startDateTime.plus({minutes: 30}),
            isAllDay: newIsAllDay,
          });
        }}
      >
        <Trans id="calendar-date-editor-allday">All day</Trans>
      </CheckBox>
      <RecurrenceDropdownContainer
        masterRecurrence={props.masterRecurrence}
        recurrence={props.recurrence}
        startDateTime={props.startDateTime}
        onChange={props.onRecurrenceChange}
      />
    </StyledOtherSettingsDiv>
  );
}
